import type { Href } from "expo-router";
import { useLocalSearchParams, useRouter } from "expo-router";
import { useEffect, useState } from "react";
import { Platform, Pressable, Text, View } from "react-native";
import { COLORS, FONTS } from "../../lib/brand";

import { AuthCard } from "../../components/auth/AuthCard";
import { Button, Input } from "../../components/ui";
import { redirectIfMfaIncomplete } from "../../lib/adminMfaRouting";
import { isAdminRole, loadLoginProfile, roleToRoute, signInWithEmail, signOut, tryRecoverSignupProvisioning } from "../../lib/auth";
import { getRememberMePreference, setRememberMePreference } from "../../lib/authStorage";
import { getMyJudgeAssignments } from "../../lib/judges";
import {
  acceptInvite,
  clearPendingInviteToken,
  getPendingInviteToken,
  isEmailMismatchAcceptError,
  portalHrefAfterAccept,
} from "../../lib/invites";

function firstParam(v: string | string[] | undefined): string | undefined {
  if (v == null) return undefined;
  return Array.isArray(v) ? v[0] : v;
}

const FOREST = COLORS.FOREST.hex;
const AMBER = COLORS.AMBER.hex;
const SUBTITLE = "rgba(28,43,26,0.68)";

export default function LoginScreen() {
  const router = useRouter();
  const params = useLocalSearchParams<{
    email?: string | string[];
    token?: string | string[];
    next?: string | string[];
  }>();
  const paramEmail = firstParam(params.email);
  const paramToken = firstParam(params.token);
  // `next=/path` lets us bounce a signed-out user through login and back to
  // a specific destination — used by the team-invite acceptance page so the
  // captain's QR/link round-trips cleanly through sign-in.
  const nextParam = firstParam(params.next);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);

  useEffect(() => {
    void (async () => {
      const r = await getRememberMePreference();
      setRememberMe(r);
    })();
  }, []);

  useEffect(() => {
    if (paramEmail) setEmail(paramEmail.trim());
  }, [paramEmail]);

  async function onSubmit() {
    setError(null);
    setBusy(true);
    try {
      setRememberMePreference(rememberMe);
      const { error: signErr } = await signInWithEmail(email.trim(), password);
      if (signErr) {
        setError(signErr.message);
        return;
      }

      const inviteToken = (paramToken ?? (await getPendingInviteToken()))?.trim() || null;
      if (inviteToken) {
        try {
          const result = await acceptInvite(inviteToken);
          await clearPendingInviteToken();
          // Judge invites don't insert into `user_roles` — only update the
          // existing `judges` row. JudgePortalGate verifies via
          // getMyJudgeAssignments(), so route to /judge without requiring a
          // role row to exist.
          if (result.kind === "judge") {
            router.replace(portalHrefAfterAccept(result));
            return;
          }
          const profile = await loadLoginProfile();
          if (!profile?.role) {
            setError("No role found for this account.");
            return;
          }
          const href = portalHrefAfterAccept(result);
          const pendingOnboarding =
            isAdminRole(profile.role) &&
            profile.employeeStatus === "pending" &&
            profile.user.user_metadata?.employee_onboard_completed !== true;
          if (pendingOnboarding && String(href).startsWith("/onboard")) {
            router.replace(href);
            return;
          }
          // Applies to anyone whose mfa_required is true - admins by default,
          // non-admins who've opted in via Settings -> Security.
          const redirected = await redirectIfMfaIncomplete(router, {
            role: profile.role,
            mfaRequired: profile.mfaRequired,
          });
          if (redirected) return;
          router.replace(href);
          return;
        } catch (e) {
          const msg = e instanceof Error ? e.message : "Could not accept invite";
          if (isEmailMismatchAcceptError(msg)) {
            await signOut();
            await clearPendingInviteToken();
            setError(
              "This invite was sent to a different email. Sign in with the email address this invite was sent to, or contact the person who invited you."
            );
            return;
          }
          setError(msg);
        }
      }

      let profile = await loadLoginProfile();
      if (!profile?.role) {
        // Recovery path: user signed up but never finished provisioning (most
        // commonly because email confirmation arrived later in a fresh tab,
        // bypassing the signup form's RPC call). Use signup metadata to
        // idempotently retry provisioning.
        const recovered = await tryRecoverSignupProvisioning();
        if (recovered) {
          profile = await loadLoginProfile();
        }
      }
      if (!profile?.role) {
        const judgeAssignments = await getMyJudgeAssignments();
        if (judgeAssignments.length > 0) {
          router.replace("/judge" as Href);
          return;
        }
        setError("No role found for this account.");
        return;
      }

      const pendingOnboarding =
        isAdminRole(profile.role) &&
        profile.employeeStatus === "pending" &&
        profile.user.user_metadata?.employee_onboard_completed !== true;
      if (pendingOnboarding) {
        router.replace("/onboard/employee" as Href);
        return;
      }

      // Applies to anyone whose mfa_required is true - admins by default,
      // non-admins who've opted in via Settings -> Security.
      const redirected = await redirectIfMfaIncomplete(router, {
        role: profile.role,
        mfaRequired: profile.mfaRequired,
      });
      if (redirected) return;

      // Honor `?next=/some/path` for in-app deep links (e.g. team invite
      // acceptance bouncing through login). Only allow same-origin paths.
      if (nextParam && nextParam.startsWith("/") && !nextParam.startsWith("//")) {
        router.replace(nextParam as Href);
        return;
      }

      router.replace(roleToRoute(profile.role) as Href);
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthCard>
      <Text
        className="mb-2 text-[10px] font-bold uppercase tracking-widest-3"
        style={{ fontFamily: FONTS.bodyBold, color: AMBER, letterSpacing: 2 }}
      >
        WELCOME BACK
      </Text>
      <Text className="text-[28px] leading-tight text-text" style={{ fontFamily: FONTS.display }}>
        Welcome back.
      </Text>
      <Text className="mt-2 text-[14px] leading-6" style={{ fontFamily: FONTS.bodyLight, color: SUBTITLE }}>
        Sign in to access your competitions, submissions, and recruiting profile.
      </Text>

      <View className="mb-6 mt-6 flex-row border-b border-sand-dark">
        <Pressable className="pb-2.5 pl-0 pr-4 pt-2">
          <Text
            className="text-[11px] font-bold uppercase tracking-wide"
            style={{
              fontFamily: FONTS.bodyBold,
              color: FOREST,
              borderBottomWidth: 2,
              borderBottomColor: AMBER,
              paddingBottom: 8,
              marginBottom: -1,
            }}
          >
            SIGN IN
          </Text>
        </Pressable>
        <Pressable
          onPress={() => router.push("/signup" as Href)}
          className="pb-2.5 pl-2 pr-2 pt-2"
          style={Platform.OS === "web" ? ({ cursor: "pointer" } as const) : {}}
        >
          <Text className="text-[11px] font-bold uppercase tracking-wide text-text/45" style={{ fontFamily: FONTS.bodyBold }}>
            CREATE ACCOUNT
          </Text>
        </Pressable>
      </View>

      <Input
        label="Email"
        value={email}
        onChangeText={setEmail}
        placeholder="you@example.com"
        keyboardType="email-address"
        autoComplete="email"
        textContentType="emailAddress"
        returnKeyType="next"
        onSubmitEditing={() => void onSubmit()}
      />

      <View className="mt-3">
        <Input
          label="Password"
          value={password}
          onChangeText={setPassword}
          placeholder="••••••••"
          secureTextEntry
          autoComplete="current-password"
          textContentType="password"
          returnKeyType="go"
          onSubmitEditing={() => void onSubmit()}
        />
      </View>

      <Pressable
        onPress={() => setRememberMe(!rememberMe)}
        className="mt-4 flex-row items-center gap-2 self-start"
        style={Platform.OS === "web" ? ({ cursor: "pointer" } as const) : {}}
        accessibilityRole="checkbox"
        accessibilityState={{ checked: rememberMe }}
      >
        <View
          className="h-4 w-4 items-center justify-center rounded border"
          style={{
            borderColor: FOREST,
            backgroundColor: rememberMe ? AMBER : "transparent",
          }}
        >
          {rememberMe ? (
            <Text className="text-[10px] font-bold text-white" style={{ fontFamily: FONTS.bodyBold }}>
              ✓
            </Text>
          ) : null}
        </View>
        <Text className="text-[13px] text-text" style={{ fontFamily: FONTS.body }}>
          Remember me
        </Text>
      </Pressable>

      {error ? (
        <Text className="mb-3 mt-3 text-center text-sm text-red-600" style={{ fontFamily: FONTS.body }}>
          {error}
        </Text>
      ) : null}

      <View className="mt-2">
        <Button variant="primary" loading={busy} onPress={() => void onSubmit()}>
          SIGN IN →
        </Button>
      </View>

      <Pressable onPress={() => router.push("/forgot-password" as Href)} className="mt-4 self-center" style={Platform.OS === "web" ? ({ cursor: "pointer" } as const) : {}}>
        <Text className="text-center text-[12px]" style={{ fontFamily: FONTS.bodyMedium, color: AMBER }}>
          Forgot password?
        </Text>
      </Pressable>
    </AuthCard>
  );
}
